'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { loginAction } from '@/app/actions/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Lock, UserRound, LogIn, AlertCircle, Eye, EyeOff } from 'lucide-react'

export default function LoginPage() {
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const formData = new FormData(e.currentTarget)
      const result = await loginAction(formData)

      if (result.success) {
        window.location.href = '/'
      } else {
        setError(result.error || 'Có lỗi xảy ra')
        setLoading(false)
      }
    } catch (err) {
      setError('Mất kết nối tới máy chủ')
      setLoading(false)
    }
  }

  return (
    <div className="h-screen h-[100dvh] w-screen overflow-hidden flex bg-white font-sans">
      {/* Left Panel - Branding (Desktop Only) */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-slate-950 p-14 relative overflow-hidden isolate border-r border-slate-900 shadow-2xl z-10">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay"></div>
        <div className="absolute top-[20%] -left-[20%] w-[500px] h-[500px] bg-emerald-500/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-[10%] -right-[10%] w-[400px] h-[400px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-[60%] left-[20%] w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-[100px] pointer-events-none" />

        <div className="relative z-10 text-white font-black text-xl tracking-widest uppercase flex items-center gap-3">
          <div className="w-11 h-11 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-2xl flex items-center justify-center shadow-[0_0_30px_rgba(16,185,129,0.3)]">
             <Lock className="w-5 h-5 text-white" />
          </div>
          LÊ HẢI
        </div>
        
        <div className="relative z-10 w-full max-w-lg mb-12">
          <div className="inline-block px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md mb-8 text-emerald-300 text-sm font-bold tracking-widest uppercase">
            Internal System
          </div>
          <h2 className="text-4xl xl:text-5xl font-black text-white leading-[1.15] mb-6 tracking-tight">
            Nền tảng Quản lý Kho <br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-emerald-400 to-teal-400">
              Đẳng cấp & Bảo mật
            </span>
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed font-medium max-w-md">
            Hệ thống nội bộ được thiết kế lưu trữ đặc quyền dành riêng cho ban quản lý kho và đối soát của Lê Hải.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-[55%] flex items-center justify-center px-4 sm:px-8 relative bg-gray-50/50 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-white via-transparent to-transparent opacity-60 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-emerald-100/40 rounded-full blur-3xl opacity-60 pointer-events-none" />

        <div className="relative w-full max-w-[400px] bg-white lg:bg-transparent rounded-2xl lg:rounded-none p-5 sm:p-7 lg:p-0 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] lg:shadow-none border border-gray-100 lg:border-none z-10">
          
          {/* Mobile Header */}
          <div className="lg:hidden mb-5 sm:mb-7 text-center">
            <div className="mx-auto w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/30 mb-3">
               <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-gray-900 tracking-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-500">LÊ HẢI</span>
            </h1>
            <p className="text-[10px] sm:text-xs text-gray-400 mt-1 font-medium">Hệ thống quản lý nội bộ</p>
          </div>

          {/* Desktop Header */}
          <div className="hidden lg:block mb-10">
            <h1 className="text-[2rem] font-black text-gray-900 tracking-tight">Đăng Nhập</h1>
            <p className="mt-2 text-[15px] text-gray-500 font-medium">Dữ liệu được mã hóa đa tầng. Nhập thông tin để tiếp tục.</p>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 text-red-600 px-3 py-2 rounded-xl text-[12px] sm:text-[13px] font-bold flex items-center gap-2 border border-red-100">
               <AlertCircle className="w-4 h-4 shrink-0" />
               <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4" suppressHydrationWarning>
             <div className="space-y-1.5">
               <Label className="text-gray-900 font-bold ml-1 text-[10px] sm:text-[12px] uppercase tracking-wider">Tên Đăng Nhập</Label>
               <div className="relative group">
                 <UserRound className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-[18px] sm:h-[18px] text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
                 <Input 
                   name="username" 
                   type="text" 
                   required 
                   autoFocus
                   placeholder="Nhập tài khoản..."
                   className="pl-9 sm:pl-11 py-2.5 sm:py-3 lg:py-4 rounded-xl bg-white border-gray-200 focus-visible:bg-white focus-visible:ring-emerald-500/20 focus-visible:border-emerald-500 transition-all font-semibold shadow-sm text-gray-800 placeholder:font-normal placeholder:text-gray-400 text-sm h-auto"
                 />
               </div>
             </div>

             <div className="space-y-1.5">
               <Label className="text-gray-900 font-bold ml-1 text-[10px] sm:text-[12px] uppercase tracking-wider">Mật Khẩu</Label>
               <div className="relative group flex items-center">
                 <Lock className="absolute left-3 sm:left-4 w-4 h-4 sm:w-[18px] sm:h-[18px] text-gray-400 group-focus-within:text-emerald-500 transition-colors z-10" />
                 <Input 
                   name="password" 
                   type={showPassword ? "text" : "password"} 
                   required 
                   placeholder="••••••••••"
                   className="pl-9 sm:pl-11 pr-10 sm:pr-11 py-2.5 sm:py-3 lg:py-4 rounded-xl bg-white border-gray-200 focus-visible:bg-white focus-visible:ring-emerald-500/20 focus-visible:border-emerald-500 transition-all font-bold tracking-widest text-emerald-900 shadow-sm placeholder:font-normal placeholder:tracking-normal placeholder:text-gray-400 w-full text-sm h-auto"
                 />
                 <button 
                   type="button" 
                   tabIndex={-1}
                   onClick={() => setShowPassword(!showPassword)}
                   className="absolute right-3 p-0.5 rounded-full text-gray-400 hover:text-emerald-600 transition-colors focus:outline-none z-10"
                 >
                   {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                 </button>
               </div>
             </div>

             <Button 
               type="submit" 
               disabled={loading}
               className="w-full h-10 sm:h-11 lg:h-12 rounded-xl bg-gray-900 hover:bg-emerald-600 text-white font-bold text-sm shadow-[0_8px_20px_rgba(0,0,0,0.1)] hover:shadow-[0_8px_25px_rgba(16,185,129,0.3)] mt-3 sm:mt-4 transition-all duration-300 group"
             >
               {loading ? 'Đang xác minh...' : 'ĐĂNG NHẬP'}
               {!loading && <LogIn className="w-4 h-4 ml-2 opacity-70 group-hover:translate-x-1 group-hover:opacity-100 transition-all" />}
             </Button>
          </form>
          
          <div className="mt-5 sm:mt-8 text-center lg:text-left text-[10px] sm:text-[12px] text-gray-400 font-medium">
            &copy; {new Date().getFullYear()} Lê Hải. Nền tảng nội bộ.
          </div>
        </div>
      </div>
    </div>
  )
}
